// Logica pura de la nomina del proveedor (PP-22): del borrador del formulario
// al cuerpo del request, y el orden de las filas. Sin React ni reloj; los
// requests salen como los tipos de @ucorp/contracts y el cliente HTTP los
// vuelve a pasar por el parser antes de mandarlos.
//
// Politica de ausencia del contrato: en la edicion, un campo que no cambio no
// viaja, y un campo opcional que se vacio viaja como null (se borra). El
// codigo de movil es obligatorio: en la edicion no se puede vaciar (PP-22).
import type { Driver, SiteVehicle, UpdateDriverRequest, UpdateVehicleRequest, VehicleType } from "@ucorp/contracts";
import type { DriverDraftRequest, VehicleDraftRequest } from "../fleet/port.js";

export const VEHICLE_TYPE_LABEL: Record<VehicleType, string> = {
  shuttle: "Combi",
  cab: "Remis",
  bus: "Colectivo",
};

export const VEHICLE_TYPES: readonly VehicleType[] = ["shuttle", "cab", "bus"];

/** Lo que tiene el formulario de vehiculo, todo como texto. `holderId` vacio: la empresa madre. */
export interface VehicleDraft {
  code: string;
  holderId: string;
  type: VehicleType;
  capacity: string;
  plate: string;
  year: string;
}

export interface DriverDraft {
  code: string;
  holderId: string;
  name: string;
  idDocument: string;
  phone: string;
}

export const EMPTY_VEHICLE_DRAFT: VehicleDraft = { code: "", holderId: "", type: "shuttle", capacity: "", plate: "", year: "" };
export const EMPTY_DRIVER_DRAFT: DriverDraft = { code: "", holderId: "", name: "", idDocument: "", phone: "" };

/** Entero positivo escrito a mano; undefined si no lo es. */
function wholeNumber(text: string): number | undefined {
  const trimmed = text.trim();
  if (!/^\d+$/.test(trimmed)) return undefined;
  return Number(trimmed);
}

/** Patente sin espacios y en mayuscula: la unicidad la decide la API. */
export function normalizePlate(plate: string): string {
  return plate.replace(/\s+/g, "").toUpperCase();
}

export function vehicleDraftFrom(vehicle: SiteVehicle): VehicleDraft {
  return {
    code: vehicle.code,
    holderId: "",
    type: vehicle.type,
    capacity: String(vehicle.capacity),
    plate: vehicle.plate,
    year: vehicle.year === undefined ? "" : String(vehicle.year),
  };
}

export function driverDraftFrom(driver: Driver): DriverDraft {
  return {
    code: driver.code,
    holderId: "",
    name: driver.name,
    idDocument: driver.idDocument ?? "",
    phone: driver.phone ?? "",
  };
}

/** Alta de vehiculo; null si falta un dato obligatorio o un numero no es numero. */
export function vehicleCreateRequest(draft: VehicleDraft): VehicleDraftRequest | null {
  const capacity = wholeNumber(draft.capacity);
  const plate = normalizePlate(draft.plate);
  const year = draft.year.trim() === "" ? undefined : wholeNumber(draft.year);
  if (capacity === undefined || capacity < 1 || plate === "") return null;
  if (draft.year.trim() !== "" && year === undefined) return null;
  const code = draft.code.trim();
  const holderId = draft.holderId.trim();
  return {
    type: draft.type,
    capacity,
    plate,
    ...(year === undefined ? {} : { year }),
    ...(code === "" ? {} : { code }),
    ...(holderId === "" ? {} : { holderId }),
  };
}

/**
 * Edicion de vehiculo contra la fila actual: solo lo que cambio. null si hay
 * un dato invalido; objeto vacio si no hay nada para guardar.
 */
export function vehicleUpdateRequest(draft: VehicleDraft, current: SiteVehicle): UpdateVehicleRequest | null {
  const capacity = wholeNumber(draft.capacity);
  const plate = normalizePlate(draft.plate);
  const code = draft.code.trim();
  if (capacity === undefined || capacity < 1 || plate === "" || code === "") return null;
  const yearText = draft.year.trim();
  const year = yearText === "" ? null : wholeNumber(yearText);
  if (year === undefined) return null;
  const currentYear = current.year ?? null;
  return {
    ...(draft.type === current.type ? {} : { type: draft.type }),
    ...(capacity === current.capacity ? {} : { capacity }),
    ...(plate === current.plate ? {} : { plate }),
    ...(year === currentYear ? {} : { year }),
    ...(code === current.code ? {} : { code }),
  };
}

export function driverCreateRequest(draft: DriverDraft): DriverDraftRequest | null {
  const name = draft.name.trim();
  if (name === "") return null;
  const code = draft.code.trim();
  const holderId = draft.holderId.trim();
  const idDocument = draft.idDocument.trim();
  const phone = draft.phone.trim();
  return {
    name,
    ...(code === "" ? {} : { code }),
    ...(holderId === "" ? {} : { holderId }),
    ...(idDocument === "" ? {} : { idDocument }),
    ...(phone === "" ? {} : { phone }),
  };
}

export function driverUpdateRequest(draft: DriverDraft, current: Driver): UpdateDriverRequest | null {
  const name = draft.name.trim();
  const code = draft.code.trim();
  if (name === "" || code === "") return null;
  const idDocument = draft.idDocument.trim() === "" ? null : draft.idDocument.trim();
  const phone = draft.phone.trim() === "" ? null : draft.phone.trim();
  return {
    ...(name === current.name ? {} : { name }),
    ...(idDocument === (current.idDocument ?? null) ? {} : { idDocument }),
    ...(phone === (current.phone ?? null) ? {} : { phone }),
    ...(code === current.code ? {} : { code }),
  };
}

/** Sin cambios para guardar. */
export function isEmptyUpdate(update: object): boolean {
  return Object.keys(update).length === 0;
}

/**
 * Orden de la lista: primero los que estan en la nomina, despues las bajas;
 * dentro de cada grupo por codigo de movil. Comparacion por codigo de punto,
 * no por locale del navegador, para que el orden sea el mismo en toda maquina.
 */
export function sortRoster<T extends { code: string; id: string; inRoster: boolean }>(rows: readonly T[]): T[] {
  return [...rows].sort((a, b) => {
    if (a.inRoster !== b.inRoster) return a.inRoster ? -1 : 1;
    if (a.code !== b.code) return a.code < b.code ? -1 : 1;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
}

/** Reemplaza una fila por la version que devolvio la API, o la agrega al final. */
export function upsertRow<T extends { id: string }>(rows: readonly T[], row: T): T[] {
  const index = rows.findIndex((r) => r.id === row.id);
  if (index === -1) return [...rows, row];
  return rows.map((r, i) => (i === index ? row : r));
}
